import { useMemo } from "react";
import { Link } from "react-router-dom";
import { createSvgIcon, styled } from "@mui/material";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import EditionBadgeCanvas from "../EditionBadge";
import { PulseCircle } from "../FlexComponents";
import { landDetailLink } from "../../config";
import { LandToken } from "../../types/LandToken";
import { formatValue } from "../../utils/formatValue";
import { landImageSrc } from "../../utils/landImage";
import { AnimatedLandCanvas, useAnimatedLand } from "../AnimatedLand";
import { landStatusStates } from "../../utils/landMeta";
import { resolveStandardStatus } from "../../utils/standard-status";
import { FadeInImg } from "./StaticMapBackground";
import {
  HERO_EASE,
  HERO_ENTER_MS,
  HERO_IMAGE_MS,
  HERO_STAGGER_MS,
  HERO_SWAP_MS,
  HeroMotionMode,
  riseSx,
} from "./heroMotion";
import { u } from "./heroScale";
import { INTER, INTER_BLACK, INTER_BOLD, MONO, MONO_BOLD } from "./heroFonts";

export type ScaleBarSpec = { label: string; px: number };

type LandSlideProps = {
  land: LandToken;
  isMobile: boolean;
  /** Mobile scale (heroScale.mobileScale) for the badge canvas, which needs a number; 1 on desktop. */
  unit: number;
  /** "enter" on page load (long staggered rise); "next"/"prev" when the carousel changes land. */
  mode: HeroMotionMode;
  /** Desktop only; null hides it (no map on screen). */
  scale: ScaleBarSpec | null;
};

// On page load the overlays share the copy column's beats (badge/pill with the divider, photo
// with the body, name and scale with the buttons), so both columns enter side by side.
const ENTER_OFFSET = 1;

/**
 * Everything on the map panel that belongs to one land: edition badge, public status, the
 * land's own photo, its name and place, and the scale bar. The place line is static, as in the
 * design (the EcoBank hero cycles place/coordinates/size/elevation there); it falls back to the
 * next state landStatusStates has when a land has no place name. Remounted (keyed by land) on a
 * carousel swap so the lines rise in again.
 *
 * Status and badge rules are the EcoBank hero's (pages/EcoBank/Hero.tsx), unchanged: the badge
 * only for an edition, lit while sold with trees left; the pill's text, colour and icon come
 * from the public standard status.
 */
const LandSlide = ({ land, isMobile, unit, mode, scale }: LandSlideProps) => {
  const step = (n: number) => riseSx(mode === "enter" ? ENTER_OFFSET + n : n, mode);

  const statusStates = useMemo(() => landStatusStates(land.meta), [land.meta]);

  const isSold = land.etNft?.status === "sold";
  const remTrees =
    formatValue(land.etNft?.easementActivated?.treeTotal as any, false) -
    (formatValue(land.etNft?.easementActivated?.rewardClaimed as any, false) || 0);
  const isActive = isSold && remTrees > 0;

  const publicStatus = resolveStandardStatus(
    { status: land.status, etNft: land.etNft, offerStatus: land.offerStatus, meta: land.meta },
    { role: "public" }
  );
  const isGreen = publicStatus?.color === "action.pulse";
  const pillText = isGreen ? "#ccff99" : "rgba(255,255,255,0.6)";
  const pillBorder = isGreen ? "#ccff99" : "rgba(255,255,255,0.35)";

  // The static poster (~200KB) paints first. The land's animated image (contentAnimated ≈ 32MB,
  // raw GIF ≈ 80MB) is never put in an <img>: <img> shows nothing until the whole file has
  // downloaded (seconds of black tile on a cold load) and then decodes it on the main thread.
  // A worker streams and plays it onto a canvas over the poster instead (components/AnimatedLand),
  // starting once this slide's entrance has played so it never competes with it.
  const photo = landImageSrc(land, "poster");
  const animation = useAnimatedLand(landImageSrc(land, "full"), {
    startDelayMs: mode === "enter" ? HERO_ENTER_MS + 3 * HERO_STAGGER_MS : HERO_SWAP_MS + 3 * HERO_STAGGER_MS,
  });

  return (
    <Slide $isMobile={isMobile}>
      {land.etNft?.editionId && (
        <Badge $isMobile={isMobile} sx={step(0)} onClick={(e) => e.stopPropagation()}>
          <EditionBadgeCanvas
            editionId={land.etNft.editionId}
            active={isActive}
            size={isMobile ? Math.round(32 * unit) : 48}
          />
        </Badge>
      )}
      {publicStatus && (
        <Pill $isMobile={isMobile} sx={{ ...step(0), color: pillText, borderColor: pillBorder }}>
          {publicStatus.label === "Active" && <PulseCircle sold={true} sx={{ mr: 0 }} />}
          {publicStatus.label === "Pending" && <PulseCircle warning sx={{ mr: 0 }} />}
          {(publicStatus.label === "Offer" || publicStatus.label === "Auction") && (
            <ListAltOutlinedIcon sx={{ fontSize: isMobile ? 16 : 21 }} />
          )}
          {publicStatus.label === "Idle" && <IdleIcon sx={{ fontSize: isMobile ? 16 : 21 }} />}
          <span>{publicStatus.label}</span>
        </Pill>
      )}
      {photo && (
        <Photo $isMobile={isMobile} sx={step(1)}>
          <FadeInImg src={photo} />
          <PhotoAnimation $playing={animation.playing}>
            <AnimatedLandCanvas ref={animation.canvasRef} aria-hidden="true" style={{ opacity: 0.8 }} />
          </PhotoAnimation>
        </Photo>
      )}
      <Bottom $isMobile={isMobile}>
        <NameBlock sx={step(2)}>
          <Name to={landDetailLink(land.id)} onClick={(e) => e.stopPropagation()}>
            {land.meta?.name}
          </Name>
          {statusStates.length > 0 && <Place>{statusStates[0]}</Place>}
        </NameBlock>
        {!isMobile && scale && (
          <Scale sx={step(2)} aria-label={`Map scale: ${scale.label}`}>
            <span>{scale.label}</span>
            <ScaleBar style={{ width: scale.px }} />
          </Scale>
        )}
      </Bottom>
    </Slide>
  );
};

const Slide = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>({
  position: "absolute",
  inset: 0,
});

// Badge and pill share a centre line (Figma: 76px down on desktop, 40px on mobile). Mobile
// positions are Figma-frame pixels in hero units (heroScale); the pill itself, being text, keeps
// its design size and centres on that line.
const Badge = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ $isMobile }) => ({
  position: "absolute",
  left: $isMobile ? u(22) : 58,
  top: $isMobile ? u(24) : 52,
  zIndex: 3,
}));

const Pill = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ $isMobile }) => ({
  position: "absolute",
  left: $isMobile ? u(71) : 147,
  top: $isMobile ? `calc(${u(40)} - 15.5px)` : 56,
  height: $isMobile ? 31 : 41,
  padding: $isMobile ? "0 14px 0 12px" : "0 18px 0 16px",
  display: "flex",
  alignItems: "center",
  gap: $isMobile ? 6 : 8,
  boxSizing: "border-box",
  backgroundColor: "rgba(43, 53, 1, 0.25)",
  border: "1px solid",
  borderRadius: 20,
  fontFamily: MONO,
  fontWeight: MONO_BOLD,
  fontSize: 12,
  lineHeight: 1,
  textTransform: "uppercase",
  zIndex: 3,
}));

// The land's own photo: square, 280px of a 1040px panel (27%) — 118px of the 319px mobile
// frame (37%) — black under 80%.
const Photo = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ $isMobile }) => ({
  position: "absolute",
  top: $isMobile ? u(25) : 52,
  right: $isMobile ? u(23) : "clamp(24px, 5.2%, 54px)",
  width: $isMobile ? u(118) : "min(280px, 27%)",
  aspectRatio: "1 / 1",
  borderRadius: $isMobile ? u(20) : 20,
  overflow: "hidden",
  backgroundColor: "#000",
  zIndex: 2,
}));

// The animation layer over the poster. Its own black base, with the canvas at 80% on top,
// keeps the photo's black-under-80% treatment; fading the whole layer in is then a plain
// cross-fade from the poster to the moving image ("Skeleton reveal", like the photo itself).
const PhotoAnimation = styled("div", {
  shouldForwardProp: (prop) => prop !== "$playing",
})<{ $playing: boolean }>(({ $playing }) => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "#000",
  opacity: $playing ? 1 : 0,
  transition: `opacity ${HERO_IMAGE_MS}ms ${HERO_EASE}`,
  pointerEvents: "none",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

const Bottom = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ $isMobile }) => ({
  position: "absolute",
  left: $isMobile ? u(23) : 51,
  right: $isMobile ? u(23) : 51,
  bottom: $isMobile ? u(65) : 47,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: 24,
  zIndex: 2,
}));

const NameBlock = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 3,
  minWidth: 0,
  color: "#fff",
});

const Name = styled(Link)({
  fontFamily: INTER,
  fontWeight: INTER_BOLD,
  fontSize: 26,
  lineHeight: "28.6px",
  letterSpacing: "-1.04px",
  color: "#fff",
  textDecoration: "none",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "&:focus-visible": { outline: "2px solid #ccff99", outlineOffset: 2, borderRadius: 4 },
});

const Place = styled("div")({
  fontFamily: INTER,
  fontWeight: INTER_BLACK,
  fontSize: 12,
  lineHeight: "15px",
  color: "#fff",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const Scale = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 17,
  // Lines the bar up with the dots' centre line rather than the name's baseline.
  marginBottom: 7,
  fontFamily: MONO,
  fontWeight: MONO_BOLD,
  fontSize: 12,
  lineHeight: "16px",
  color: "#fff",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
});

// The design's open-topped bracket (2px white, 7px tall); its width is the measured distance.
const ScaleBar = styled("span")({
  display: "block",
  height: 7,
  boxSizing: "border-box",
  border: "2px solid #fff",
  borderTop: "none",
});

// Same glyph as the EcoBank hero's Idle icon.
const IdleIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
    {/* eslint-disable-next-line max-len */}
    <path d="M140-440q-25 0-42.5-17.5T80-500q0-25 17.5-42.5T140-560h240q25 0 42.5 17.5T440-500q0 25-17.5 42.5T380-440H140Zm440 0q-25 0-42.5-17.5T520-500q0-25 17.5-42.5T580-560h240q25 0 42.5 17.5T880-500q0 25-17.5 42.5T820-440H580Z" />
  </svg>,
  "Idle"
);

export default LandSlide;
